/**
 * Datum-/tidsverktyg för konsekvent tidszonshantering.
 *
 * Bakgrund: Backend lagrar tider i UTC men vissa databasfält returneras utan
 * tidszons-marker (t.ex. "2026-08-14T12:30:00" istället för "2026-08-14T12:30:00+00:00").
 * `new Date("2026-08-14T12:30:00")` tolkas då som LOKAL tid av JS —
 * vilket ger 1-2h fel i Sverige. Använd dessa helpers istället.
 */

const HAS_TZ_MARKER_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Tolka en ISO-sträng som UTC om ingen tidszons-marker finns.
 * Om markern finns respekteras den.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function parseServerDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  // Om det saknas TZ-marker antar vi UTC (backend lagrar allt i UTC)
  const withTz = HAS_TZ_MARKER_RE.test(s) ? s : (s.includes('T') ? s + 'Z' : s);
  const d = new Date(withTz);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formatera en tidpunkt i Europe/Stockholm-tid med svensk locale.
 * @param {string|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} opts
 */
export function fmtDateTime(value, opts = { dateStyle: 'short', timeStyle: 'short' }) {
  const d = parseServerDate(value);
  if (!d) return '';
  return d.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm', ...opts });
}

/**
 * Formatera bara tiden (HH:MM) i svenskt lokalt format.
 */
export function fmtTime(value) {
  const d = parseServerDate(value);
  if (!d) return '';
  return d.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm', timeStyle: 'short' });
}

/**
 * Formatera bara datumet (YYYY-MM-DD) i Stockholm-tid.
 */
export function fmtDate(value) {
  const d = parseServerDate(value);
  if (!d) return '';
  return d.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm', dateStyle: 'short' });
}
